// This file is automatically generated. Do not edit it directly.
import { createClient, navigatorLock } from '@supabase/supabase-js';
import type { Database } from './types';
import { createSessionLock } from './sessionLock';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    'Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must be set in your .env file'
  );
}

// Log environment variables on startup (for debugging, dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  console.log('[Supabase Client] Initializing with:');
  console.log('[Supabase Client] URL:', SUPABASE_URL ? '✓ Set' : '✗ Missing');
  console.log('[Supabase Client] Key:', SUPABASE_PUBLISHABLE_KEY ? '✓ Set' : '✗ Missing');

  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
    console.error('CRITICAL: Supabase environment variables are not set!');
    console.error('Missing:', {
      VITE_SUPABASE_URL: !SUPABASE_URL,
      VITE_SUPABASE_PUBLISHABLE_KEY: !SUPABASE_PUBLISHABLE_KEY
    });
    console.error('Available env vars:', Object.keys(import.meta.env).filter(k => k.startsWith('VITE_')));
  }
}

// Create a storage adapter that falls back gracefully if localStorage is unavailable
const createStorageAdapter = () => {
  // Try to use localStorage first
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('__storage_test__', '1');
      localStorage.removeItem('__storage_test__');
      return localStorage;
    }
  } catch (e) {
    // localStorage may be unavailable in some contexts
    console.debug('localStorage unavailable, using in-memory storage', e);
  }

  // Fallback to in-memory storage
  const store: Record<string, string> = {};
  return {
    getItem: (key: string) => store[key] ?? null,
    setItem: (key: string, value: string) => {
      store[key] = value;
    },
    removeItem: (key: string) => {
      delete store[key];
    },
  };
};

const storage = createStorageAdapter();

const sessionLock = createSessionLock(
  navigatorLock,
  (name, fn) => navigator.locks.request(name, { steal: true }, () => fn()),
);

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: storage,
    persistSession: true,
    autoRefreshToken: true,
    ...(typeof navigator !== 'undefined' && navigator.locks ? { lock: sessionLock } : {}),
  },
});

/**
 * Sign out without depending on the server. auth-js signOut() calls the
 * server for every scope and keeps the local session on any failure other
 * than 401/403/404 (GoTrueClient._signOut), so a failed logout left the
 * adapter holding a valid session while the app state was already cleared.
 * The keys are removed through the SAME adapter the client reads, which
 * covers the in-memory fallback that a bare localStorage sweep never reaches.
 */
export const forceLocalSignOut = async (): Promise<void> => {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // Falls through to the direct removal below.
  }
  // supabase-js names the session key sb-<project-ref>-auth-token; the ref is
  // the first host label. storageKey itself is protected on the auth client.
  const key = `sb-${new URL(SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
  for (const k of [key, `${key}-user`, `${key}-code-verifier`]) {
    try { await storage.removeItem(k); } catch { /* blocked storage */ }
  }
};
